import { sendAFEvent, setAFCustomerId } from './appsflyer';
import { sendAmplitudeData, setAmplitudeUserId, setAmplitudeUserProperties } from './amplitude';
import { sendBingFunnelEvent, sendBingPurchaseEvent } from './bing';
import { sendLockerStudioData } from './lockerStudio';
import {
  ENABLE_GOOGLE_ANALYTICS_EVENTS,
  IS_PROD,
  GOOGLE_MEASUREMENT_ID,
  GOOGLE_ADS_ID,
  GOOGLE_ADS_CONVERSION_ID,
  GOOGLE_ADS_LEAD_EMAIL_ID,
  GOOGLE_ADS_INTRO_ACTIVATED_FRONT_ID,
  GOOGLE_ADS_PURCHASE_FRONT_ID,
  GOOGLE_ADS_SGTM_ID,
  GOOGLE_ADS_LTV_ID_1,
  GOOGLE_ADS_LTV_CONVERSION_LABEL_1,
  GOOGLE_ADS_LTV_ID_2,
  GOOGLE_ADS_LTV_CONVERSION_LABEL_2,
  GOOGLE_ADS_LTV_ID_3,
  GOOGLE_ADS_LTV_CONVERSION_LABEL_3,
  GOOGLE_ADS_LTV_ID_4,
  GOOGLE_ADS_LTV_CONVERSION_LABEL_4,
  GOOGLE_ADS_LTV_ID_5,
  GOOGLE_ADS_LTV_CONVERSION_LABEL_5,
  GOOGLE_ADS_LTV_ID_6,
  GOOGLE_ADS_LTV_CONVERSION_LABEL_6,
  GOOGLE_ADS_LTV_ID_7,
  GOOGLE_ADS_LTV_CONVERSION_LABEL_7,
  GTM_GTG_ID,
} from '../core/constants';
import { sendGoogleTagEvent } from './googleTag';
import { isPLValueLanding, resolvePLTVCountry, sendReviewPurchaseConversion } from '../core/utils';
import { getSearchLanguage, parseOnboardingPath } from '../locales/languageConfig';
import { GOOGLE_FLOWS } from '../core/utils/googleFlow';
import { GTG_EVENTS, pushGtgEvent } from '../core/utils/gtgDataLayer';
import store from '../core/store';
import { selectFlowType, selectLanding, selectGoogleFlow } from '../core/store/plans';
import { type GoogleAdsUserData, hasGoogleAdsUserData } from './googleAdsUserData';
import { BI_EVENTS } from './biEvents';
import { getOppref, sendOpenAISubscriptionCreated } from './openai';

export { buildGoogleAdsUserData, GOOGLE_ADS_BILLING_DETAILS_KEY } from './googleAdsUserData';
export type {
  GoogleAdsUserData,
  GoogleAdsStripeBillingDetails,
  GoogleAdsGeoData,
} from './googleAdsUserData';

const SGTM_FLOW = 'sgtm';

const shouldSendGoogleEvents = IS_PROD || ENABLE_GOOGLE_ANALYTICS_EVENTS === 'true';
const getFlowType = () => selectFlowType(store.getState());
const getLanding = () => selectLanding(store.getState());
const isSgtmFlow = () => getFlowType() === SGTM_FLOW;
const isCurrentPLValueLanding = () => isPLValueLanding(getLanding());
const isGtgGoogleFlow = () => selectGoogleFlow(store.getState()) === GOOGLE_FLOWS.GTG;
const googleAdsStaticLTVConversions = [
  { id: GOOGLE_ADS_LTV_ID_1, label: GOOGLE_ADS_LTV_CONVERSION_LABEL_1 },
  { id: GOOGLE_ADS_LTV_ID_2, label: GOOGLE_ADS_LTV_CONVERSION_LABEL_2 },
  { id: GOOGLE_ADS_LTV_ID_3, label: GOOGLE_ADS_LTV_CONVERSION_LABEL_3 },
  { id: GOOGLE_ADS_LTV_ID_4, label: GOOGLE_ADS_LTV_CONVERSION_LABEL_4 },
  { id: GOOGLE_ADS_LTV_ID_5, label: GOOGLE_ADS_LTV_CONVERSION_LABEL_5 },
  { id: GOOGLE_ADS_LTV_ID_6, label: GOOGLE_ADS_LTV_CONVERSION_LABEL_6 },
];
const googleAdsDynamicLTVConversion = {
  id: GOOGLE_ADS_LTV_ID_7,
  label: GOOGLE_ADS_LTV_CONVERSION_LABEL_7,
};

const withGoogleAdsUserData = (payload: Record<string, unknown>, userData?: GoogleAdsUserData) => {
  if (userData && hasGoogleAdsUserData(userData)) {
    payload.user_data = userData;
  }
  return payload;
};

export const sendLeadEmailConversion = (userData?: GoogleAdsUserData) => {
  if (isGtgGoogleFlow()) return;
  if (!shouldSendGoogleEvents || !isSgtmFlow()) return;
  if (!GOOGLE_ADS_SGTM_ID || !GOOGLE_ADS_LEAD_EMAIL_ID) return;
  sendGoogleTagEvent(
    'conversion',
    withGoogleAdsUserData(
      {
        send_to: `${GOOGLE_ADS_SGTM_ID}/${GOOGLE_ADS_LEAD_EMAIL_ID}`,
      },
      userData,
    ),
  );
};

const sendIntroActivatedConversion = (
  value: number,
  currency = 'USD',
  transactionId: string,
  userData?: GoogleAdsUserData,
) => {
  if (!shouldSendGoogleEvents || !GOOGLE_ADS_SGTM_ID || !GOOGLE_ADS_INTRO_ACTIVATED_FRONT_ID)
    return;
  sendGoogleTagEvent(
    'conversion',
    withGoogleAdsUserData(
      {
        send_to: `${GOOGLE_ADS_SGTM_ID}/${GOOGLE_ADS_INTRO_ACTIVATED_FRONT_ID}`,
        value,
        currency,
        transaction_id: transactionId,
      },
      userData,
    ),
  );
};

const sendSgtmPurchaseConversion = (
  value: number,
  currency: string,
  transactionId: string,
  userData?: GoogleAdsUserData,
) => {
  if (!GOOGLE_ADS_SGTM_ID || !GOOGLE_ADS_PURCHASE_FRONT_ID) return;
  sendGoogleTagEvent(
    'conversion',
    withGoogleAdsUserData(
      {
        send_to: `${GOOGLE_ADS_SGTM_ID}/${GOOGLE_ADS_PURCHASE_FRONT_ID}`,
        value,
        currency,
        transaction_id: transactionId,
      },
      userData,
    ),
  );
};

type AnalyticsChannel = 'appsflyer' | 'amplitude' | 'bi' | 'gtm';

const EXCLUDED_PARAMS_BY_CHANNEL: Partial<Record<AnalyticsChannel, readonly string[]>> = {
  amplitude: ['stripe_account_name'],
};

const paramsForChannel = (channel: AnalyticsChannel, eventParams: any) => {
  const excludedParams = EXCLUDED_PARAMS_BY_CHANNEL[channel];
  if (!excludedParams || !eventParams) return eventParams;

  const params = { ...eventParams };
  excludedParams.forEach((key) => delete params[key]);
  return params;
};

export const sendAnalyticsEvents = (eventName: string, eventParams: any) => {
  const biEventName = BI_EVENTS[eventName];

  sendAFEvent(eventName, paramsForChannel('appsflyer', eventParams));
  sendAmplitudeData(eventName, paramsForChannel('amplitude', eventParams));
  sendLockerStudioData(biEventName ?? eventName, paramsForChannel('bi', eventParams));
  pushGoogleTag(eventName, paramsForChannel('gtm', eventParams));
  sendBingFunnelEvent(eventName);
};

interface PurchaseEvent {
  price: number;
  currency?: string;
  plan: string;
  sub_id?: string;
  subscriptionId?: string;
  event_id: string;
  eventsData: Record<string, unknown>;
  isUpsell?: boolean;
  isTrial?: boolean;
  pLTVPrice?: number;
  dynamicPLTVPrice?: number;
  userLocation?: string | null;
  userData?: GoogleAdsUserData;
}

export const sendPurchaseEvent = ({
  price,
  currency = 'USD',
  plan,
  sub_id,
  subscriptionId,
  event_id,
  eventsData,
  isUpsell = false,
  isTrial = false,
  pLTVPrice,
  dynamicPLTVPrice,
  userLocation,
  userData,
}: PurchaseEvent) => {
  if (pLTVPrice !== undefined) {
    const { language, linkCountry } = parseOnboardingPath(window.location.pathname);
    const linkLanguage = language ?? getSearchLanguage(window.location.search);
    const { country, source } = resolvePLTVCountry(userLocation, linkCountry);
    setAmplitudeUserProperties({
      link_language: linkLanguage ?? 'none',
      link_country: linkCountry ?? 'none',
      browser_language: navigator.language,
      detected_geo: userLocation || 'none',
      pltv_country: country ?? 'none',
      pltv_country_source: source,
    });
  }

  const { fbclid, ttclid } = eventsData;
  const transactionId = sub_id ?? subscriptionId ?? '';

  sendBingPurchaseEvent({ price, currency, eventId: event_id });

  if (fbclid) {
    pushFBEvent(
      {
        currency: currency,
        itemCount: 1,
        transactionId,
        price: price,
        value: price,
      },
      event_id,
    );
  } else if (ttclid) {
    window?.ttq?.track('CompletePayment', {
      value: price,
      currency: currency,
      contents: [
        {
          content_type: plan,
          content_name: plan,
        },
      ],
      event_id,
    });
  }

  if (getOppref() || eventsData.oppref) {
    sendOpenAISubscriptionCreated(plan, price, currency, event_id);
  }

  if (isGtgGoogleFlow()) {
    if (!GTM_GTG_ID) return;

    pushGtgEvent({
      event: GTG_EVENTS.PURCHASE,
      value: price,
      currency,
      transaction_id: transactionId,
      items: [
        {
          item_id: plan,
          item_name: plan,
        },
      ],
      ...(eventsData?.gclid ? { gclid: String(eventsData.gclid) } : {}),
      ...(userData && hasGoogleAdsUserData(userData) ? { user_data: userData } : {}),
    });

    return;
  }

  if (shouldSendGoogleEvents) {
    sendGoogleTagEvent('purchase', {
      currency: currency,
      value: price,
      transaction_id: transactionId,
      gclid: eventsData?.gclid ?? '',
      items: {
        item_id: plan,
        item_name: plan,
      },

      send_to: GOOGLE_MEASUREMENT_ID,
    });

    const sendLTVConversion = ({ id, label }: { id?: string; label?: string }, value: number) => {
      if (!id || !label) return;
      sendGoogleTagEvent(
        'conversion',
        withGoogleAdsUserData(
          {
            send_to: `${id}/${label}`,
            value,
            currency: 'USD',
            transaction_id: transactionId,
          },
          userData,
        ),
      );
    };

    if (typeof pLTVPrice === 'number' && Number.isFinite(pLTVPrice)) {
      googleAdsStaticLTVConversions.forEach((conversion) => {
        sendLTVConversion(conversion, pLTVPrice);
      });
    }

    if (!isCurrentPLValueLanding()) {
      sendGoogleTagEvent(
        'conversion',
        withGoogleAdsUserData(
          {
            send_to: `${GOOGLE_ADS_ID}/${GOOGLE_ADS_CONVERSION_ID}`,
            value: price,
            currency: currency,
            transaction_id: transactionId,
          },
          userData,
        ),
      );
    }

    if (typeof dynamicPLTVPrice === 'number' && Number.isFinite(dynamicPLTVPrice)) {
      sendLTVConversion(googleAdsDynamicLTVConversion, dynamicPLTVPrice);
    }

    if (isSgtmFlow() && !isUpsell) {
      if (isTrial) {
        sendIntroActivatedConversion(price, currency || 'USD', transactionId, userData);
      } else {
        sendSgtmPurchaseConversion(price, currency, transactionId, userData);
      }
    }

    sendReviewPurchaseConversion({
      transactionId,
      value: price,
      currency: currency,
      userData,
    });
  }
};

export const pushGoogleConversion = (
  conversionLabel: string,
  params: Record<string, unknown> = {},
  userData?: GoogleAdsUserData,
) => {
  if (isGtgGoogleFlow()) return;
  if (!shouldSendGoogleEvents) return;
  if (!GOOGLE_ADS_ID || !conversionLabel) return;

  sendGoogleTagEvent(
    'conversion',
    withGoogleAdsUserData(
      {
        send_to: `${GOOGLE_ADS_ID}/${conversionLabel}`,
        ...params,
      },
      userData,
    ),
  );
};

export const setAnalyticsUserId = (userId: string) => {
  setAmplitudeUserId(userId);
  setAFCustomerId(userId);
};

export const pushGoogleTag = (
  eventName: string,
  eventParams: any,
  tagId = GOOGLE_MEASUREMENT_ID,
) => {
  if (isGtgGoogleFlow()) return;
  if (!shouldSendGoogleEvents) return;
  sendGoogleTagEvent(eventName, { ...eventParams, send_to: tagId });
};

export const pushFBEvent = (data: any, eventId: string) => {
  window.fbq?.('track', 'Purchase' + (IS_PROD ? '' : '_test'), data, {
    eventID: eventId,
  });
};
